/**
 * Hook de gestion du thème clair / sombre.
 *
 * Source de vérité = localStorage clé "theme" ∈ {"light", "dark", "system"}.
 * "system" suit la media query `prefers-color-scheme: dark` du navigateur.
 *
 * L'effet de bord — apposer/retirer la classe `dark` sur <html> — est
 * appliqué dans un effet pour que Tailwind (`darkMode: 'class'`) prenne
 * la relève. Pas de provider, pas de contexte global : chaque composant
 * qui consomme `useTheme()` reçoit la valeur courante via un store mini.
 */

import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "theme";

function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  const effectiveDark = mode === "dark" || (mode === "system" && systemPrefersDark());
  root.classList.toggle("dark", effectiveDark);
  // color-scheme aide les éléments natifs (scrollbars, form controls) à
  // s'adapter automatiquement au thème.
  root.style.colorScheme = effectiveDark ? "dark" : "light";
}

// Mini store pub/sub partagé pour synchroniser tous les hooks useTheme()
// instanciés dans l'app (sinon chaque composant aurait son propre state).
const listeners = new Set<(m: ThemeMode) => void>();
let current: ThemeMode = getStoredTheme();

function setGlobal(mode: ThemeMode) {
  current = mode;
  window.localStorage.setItem(STORAGE_KEY, mode);
  applyTheme(mode);
  listeners.forEach((l) => l(mode));
}

// Init au boot du module : applique le thème stocké AVANT le premier render
// pour éviter le "flash" clair → sombre.
if (typeof window !== "undefined") {
  applyTheme(current);
  // Écoute les changements de media query pour répercuter "system"
  if (window.matchMedia) {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    mql.addEventListener("change", () => {
      if (current === "system") applyTheme("system");
    });
  }
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(current);

  useEffect(() => {
    const listener = (m: ThemeMode) => setMode(m);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const setTheme = useCallback((m: ThemeMode) => setGlobal(m), []);
  const toggle = useCallback(() => {
    const next: ThemeMode = mode === "dark" ? "light" : "dark";
    setGlobal(next);
  }, [mode]);

  return { mode, setTheme, toggle };
}
